import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Card, Badge } from 'react-bootstrap';
import { 
  Search, 
  MapPin, 
  Filter,
  Heart,
  Pill,
  Droplet,
  DollarSign,
  Clock,
  User,
  Shield,
  Phone,
  MessageCircle,
  Eye,
  AlertCircle,
  TrendingUp,
  Award,
  X,
  CheckCircle,
  Calendar,
  Package
} from 'lucide-react';
import '../../../assets/css/general/DonationsListing.css';
import NavigationBar from '../../../components/user/general/NavigationBar';
import Footer from '../footer/Footer';

const DonationsListing = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedCity, setSelectedCity] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState('latest');
  const [currentPage, setCurrentPage] = useState(1);
  const [emergencyOnly, setEmergencyOnly] = useState(false);
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [donorLevel, setDonorLevel] = useState('all');
  const itemsPerPage = 9;

  useEffect(() => {
    setIsVisible(true);
    const observeElements = () => {
      const elements = document.querySelectorAll('.animate-on-scroll');
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      }, { threshold: 0.1 });
      elements.forEach(el => observer.observe(el));
    };
    observeElements();
  }, []);

  // Categories
  const categories = [
    { id: 'all', name: 'All Donations', icon: Heart, color: '#1F7A7A', count: 156 },
    { id: 'food', name: 'Food', icon: Heart, color: '#FFB800', count: 42 },
    { id: 'medicine', name: 'Medicine', icon: Pill, color: '#1F7A7A', count: 38 },
    { id: 'blood', name: 'Blood', icon: Droplet, color: '#DC3545', count: 28 },
    { id: 'funds', name: 'Funds', icon: DollarSign, color: '#165C5C', count: 48 }
  ];

  // Cities
  const cities = [
    'All Cities', 'Karachi', 'Lahore', 'Islamabad', 'Rawalpindi', 
    'Faisalabad', 'Multan', 'Peshawar', 'Quetta', 'Sialkot'
  ];

  // Sample Listings
  const allListings = [
    {
      id: 1,
      type: 'food',
      title: 'Fresh Cooked Meals Available',
      description: 'Providing fresh, nutritious meals for families in need. Hot food ready for distribution daily.',
      donor: {
        name: 'Ahmed Khan',
        badge: 'gold',
        verified: true,
        avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Ahmed'
      },
      location: 'Lahore, Pakistan',
      urgency: 'normal',
      status: 'available',
      postedDate: '2 hours ago',
      quantity: '50 meals',
      contact: '+92-300-1234567'
    },
    {
      id: 2,
      type: 'blood',
      title: 'O+ Blood Urgently Needed',
      description: 'Critical patient requires O+ blood donation. Immediate requirement at Shaukat Khanum Hospital.',
      donor: {
        name: 'Fatima Ali',
        badge: 'silver',
        verified: true,
        avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Fatima'
      },
      location: 'Karachi, Pakistan',
      urgency: 'emergency',
      status: 'available',
      postedDate: '30 minutes ago',
      quantity: '2 units',
      contact: '+92-321-9876543'
    },
    {
      id: 3,
      type: 'medicine',
      title: 'Diabetes Medication Donation',
      description: 'High-quality insulin and diabetes management medications available for those in need.',
      donor: {
        name: 'Edhi Foundation',
        badge: 'ngo',
        verified: true,
        avatar: 'https://api.dicebear.com/7.x/shapes/svg?seed=Edhi'
      },
      location: 'Islamabad, Pakistan',
      urgency: 'normal',
      status: 'available',
      postedDate: '5 hours ago',
      quantity: '20 boxes',
      contact: '+92-51-1234567'
    },
    {
      id: 4,
      type: 'funds',
      title: 'Education Fund for Orphans',
      description: 'Fundraising campaign to provide education support for 100 orphan children this year.',
      donor: {
        name: 'TCF Foundation',
        badge: 'ngo',
        verified: true,
        avatar: 'https://api.dicebear.com/7.x/shapes/svg?seed=TCF'
      },
      location: 'Multiple Cities',
      urgency: 'normal',
      status: 'available',
      postedDate: '1 day ago',
      quantity: 'Target: Rs. 500,000',
      contact: '+92-42-111-823-823'
    },
    {
      id: 5,
      type: 'food',
      title: 'Iftar Meals Distribution',
      description: 'Daily iftar meal packages for 100 families during Ramadan. Fresh and hygienic preparation.',
      donor: {
        name: 'Hassan Raza',
        badge: 'bronze',
        verified: false,
        avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Hassan'
      },
      location: 'Faisalabad, Pakistan',
      urgency: 'normal',
      status: 'available',
      postedDate: '3 hours ago',
      quantity: '100 packages',
      contact: '+92-300-5555555'
    },
    {
      id: 6,
      type: 'medicine',
      title: 'Cancer Treatment Support',
      description: 'Chemotherapy medicines and pain management drugs for cancer patients.',
      donor: {
        name: 'Shaukat Khanum',
        badge: 'ngo',
        verified: true,
        avatar: 'https://api.dicebear.com/7.x/shapes/svg?seed=Shaukat'
      },
      location: 'Lahore, Pakistan',
      urgency: 'emergency',
      status: 'available',
      postedDate: '1 hour ago',
      quantity: '15 doses',
      contact: '+92-42-111-155-155'
    },
    {
      id: 7,
      type: 'blood',
      title: 'AB- Blood Required',
      description: 'Rare blood type needed for surgery patient at Civil Hospital.',
      donor: {
        name: 'Ali Raza',
        badge: 'silver',
        verified: true,
        avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Ali'
      },
      location: 'Karachi, Pakistan',
      urgency: 'emergency',
      status: 'available',
      postedDate: '45 minutes ago',
      quantity: '1 unit',
      contact: '+92-333-1111111'
    },
    {
      id: 8,
      type: 'funds',
      title: 'Clean Water Project',
      description: 'Installing water filtration systems in rural areas to provide clean drinking water.',
      donor: {
        name: 'Water Foundation',
        badge: 'ngo',
        verified: true,
        avatar: 'https://api.dicebear.com/7.x/shapes/svg?seed=Water'
      },
      location: 'Multan, Pakistan',
      urgency: 'normal',
      status: 'available',
      postedDate: '6 hours ago',
      quantity: 'Goal: Rs. 800,000',
      contact: '+92-61-1234567'
    },
    {
      id: 9,
      type: 'food',
      title: 'Weekly Grocery Packages',
      description: 'Essential grocery items for underprivileged families. Weekly distribution program.',
      donor: {
        name: 'Sara Ahmed',
        badge: 'gold',
        verified: true,
        avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Sara'
      },
      location: 'Islamabad, Pakistan',
      urgency: 'normal',
      status: 'available',
      postedDate: '4 hours ago',
      quantity: '30 packages',
      contact: '+92-51-9999999'
    }
  ];

  // Filter listings
  const filteredListings = allListings.filter(item => {
    if (selectedCategory !== 'all' && item.type !== selectedCategory) return false;
    if (selectedCity !== 'all' && !item.location.includes(selectedCity)) return false;
    if (searchQuery && !item.title.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (emergencyOnly && item.urgency !== 'emergency') return false;
    if (verifiedOnly && !item.donor.verified) return false;
    if (donorLevel !== 'all' && item.donor.badge !== donorLevel) return false;
    return true;
  }).sort((a, b) => {
    if (sortBy === 'urgent') return a.urgency === 'emergency' ? -1 : 1;
    return 0;
  });

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentListings = filteredListings.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredListings.length / itemsPerPage);

  // Get badge config
  const getBadgeConfig = (badge) => {
    const configs = {
      gold: { color: '#FFB800', icon: '👑', label: 'Gold' },
      silver: { color: '#C0C0C0', icon: '⭐', label: 'Silver' },
      bronze: { color: '#CD7F32', icon: '🏅', label: 'Bronze' },
      ngo: { color: '#1F7A7A', icon: '🏢', label: 'NGO' }
    };
    return configs[badge] || configs.bronze;
  };

  // Get type icon
  const getTypeIcon = (type) => {
    const icons = {
      food: Heart,
      medicine: Pill,
      blood: Droplet,
      funds: DollarSign
    };
    return icons[type] || Heart;
  };

  // Clear all filters
  const clearFilters = () => {
    setSelectedCategory('all');
    setSelectedCity('all');
    setSearchQuery('');
    setEmergencyOnly(false);
    setVerifiedOnly(false);
    setDonorLevel('all');
    setSortBy('latest');
  };

  return (
    <div className="listing-page">
      <NavigationBar />
      
      {/* Hero Header */}
      <div className="listing-hero">
        <Container>
          <div className={`hero-content ${isVisible ? 'fade-in-up' : ''}`}>
            <div className="hero-badge">
              <Heart size={20} fill="#FFB800" stroke="#FFB800" />
              <span>EXPLORE DONATIONS</span>
            </div>
            <h1 className="hero-title">Find & Support Causes</h1>
            <p className="hero-subtitle">
              Connect with verified donors and NGOs. Make a difference today.
            </p>

            {/* Quick Stats */}
            <div className="quick-stats">
              <div className="stat-item">
                <TrendingUp size={24} />
                <div>
                  <span className="stat-value">{filteredListings.length}</span>
                  <span className="stat-label">Active Listings</span>
                </div>
              </div>
              <div className="stat-item">
                <Shield size={24} />
                <div>
                  <span className="stat-value">98%</span>
                  <span className="stat-label">Verified</span>
                </div>
              </div>
              <div className="stat-item">
                <Clock size={24} />
                <div>
                  <span className="stat-value">24/7</span>
                  <span className="stat-label">Support</span>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </div>

      {/* Main Content */}
      <Container className="main-content">
        <Row>
          {/* Sidebar Filters */}
          <Col lg={3} className="sidebar-col">
            <div className={`filters-sidebar glass-card animate-on-scroll ${showFilters ? 'mobile-visible' : ''}`}>
              <div className="filters-header">
                <h3>
                  <Filter size={20} />
                  Filters
                </h3>
                <button className="close-filters d-lg-none" onClick={() => setShowFilters(false)}>
                  <X size={24} />
                </button>
              </div>

              {/* Category Filter */}
              <div className="filter-group">
                <label>
                  <Heart size={18} />
                  Category
                </label>
                <Form.Select 
                  value={selectedCategory} 
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="custom-select"
                >
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name} ({cat.count})
                    </option>
                  ))}
                </Form.Select>
              </div>

              {/* City Filter */}
              <div className="filter-group">
                <label>
                  <MapPin size={18} />
                  Location
                </label>
                <Form.Select 
                  value={selectedCity} 
                  onChange={(e) => setSelectedCity(e.target.value)}
                  className="custom-select"
                >
                  {cities.map(city => (
                    <option key={city} value={city === 'All Cities' ? 'all' : city}>
                      {city}
                    </option>
                  ))}
                </Form.Select>
              </div>

              {/* Donor Level Filter */}
              <div className="filter-group">
                <label>
                  <Award size={18} />
                  Donor Level
                </label>
                <Form.Select 
                  value={donorLevel} 
                  onChange={(e) => setDonorLevel(e.target.value)}
                  className="custom-select"
                >
                  <option value="all">All Levels</option>
                  <option value="gold">Gold Donors</option>
                  <option value="silver">Silver Donors</option>
                  <option value="bronze">Bronze Donors</option>
                  <option value="ngo">NGOs Only</option>
                </Form.Select>
              </div>

              {/* Sort Filter */}
              <div className="filter-group">
                <label>
                  <TrendingUp size={18} />
                  Sort By
                </label>
                <Form.Select 
                  value={sortBy} 
                  onChange={(e) => setSortBy(e.target.value)}
                  className="custom-select"
                >
                  <option value="latest">Latest First</option>
                  <option value="urgent">Urgent First</option>
                  <option value="nearby">Nearby First</option>
                </Form.Select>
              </div>

              {/* Status Filter */}
              <div className="filter-group">
                <label>Status</label>
                <div className="checkbox-group">
                  <Form.Check 
                    type="checkbox" 
                    label="Emergency Only" 
                    checked={emergencyOnly}
                    onChange={(e) => setEmergencyOnly(e.target.checked)}
                  />
                  <Form.Check 
                    type="checkbox" 
                    label="Verified Only" 
                    checked={verifiedOnly}
                    onChange={(e) => setVerifiedOnly(e.target.checked)}
                  />
                </div>
              </div>

              <button className="clear-filters" onClick={clearFilters}>
                Clear All Filters
              </button>
            </div>
          </Col>

          {/* Listings Grid */}
          <Col lg={9}>
            {/* Toolbar */}
            <div className="listings-toolbar">
              <div className="toolbar-left">
                <h2>{filteredListings.length} Donations Found</h2>
              </div>
              <div className="toolbar-right">
                <button 
                  className="filter-toggle d-lg-none"
                  onClick={() => setShowFilters(true)}
                >
                  <Filter size={20} />
                  Filters
                </button>
              </div>
            </div>

            {/* Donation Cards Grid */}
            <Row className="listings-grid">
              {currentListings.map((listing, index) => {
                const TypeIcon = getTypeIcon(listing.type);
                const badgeConfig = getBadgeConfig(listing.donor.badge);
                
                return (
                  <Col md={6} xl={4} key={listing.id} className="mb-4">
                    <Card className="donation-card glass-card animate-on-scroll">
                      {/* Status Indicator */}
                      <div className="status-indicator"></div>

                      {/* Urgency Badge */}
                      {listing.urgency === 'emergency' && (
                        <Badge className="urgency-badge emergency">
                          <AlertCircle size={12} />
                          URGENT
                        </Badge>
                      )}

                      <Card.Body className="card-body-custom">
                        {/* Card Header */}
                        <div className="card-header-custom">
                          <Badge className={`category-badge ${listing.type}`}>
                            <TypeIcon size={14} />
                            {listing.type}
                          </Badge>
                          <span className="posted-time">
                            <Clock size={12} />
                            {listing.postedDate}
                          </span>
                        </div>

                        {/* Title */}
                        <h4 className="card-title">{listing.title}</h4>

                        {/* Description */}
                        <p className="card-description">{listing.description}</p>

                        {/* Donor Info */}
                        <div className="donor-info">
                          <div className="donor-avatar">
                            <img src={listing.donor.avatar} alt={listing.donor.name} />
                            {listing.donor.verified && (
                              <div className="verified-check">
                                <CheckCircle size={12} color="#1F7A7A" fill="#1F7A7A" />
                              </div>
                            )}
                          </div>
                          <div className="donor-details">
                            <div className="donor-name">
                              {listing.donor.name}
                              <span className="donor-badge-mini" style={{ background: badgeConfig.color }}>
                                {badgeConfig.icon}
                              </span>
                            </div>
                            <div className="donor-location">
                              <MapPin size={12} />
                              {listing.location}
                            </div>
                          </div>
                        </div>

                        {/* Quantity */}
                        <div className="quantity-info">
                          <Package size={16} />
                          <span>{listing.quantity}</span>
                        </div>

                        {/* Actions */}
                        <div className="card-actions">
                          <button className="btn-primary">
                            <Eye size={16} />
                            View Details
                          </button>
                          <button className="btn-secondary">
                            <MessageCircle size={16} />
                          </button>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination-container">
                <button 
                  className="page-btn" 
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                
                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i + 1}
                    className={`page-number ${currentPage === i + 1 ? 'active' : ''}`}
                    onClick={() => setCurrentPage(i + 1)}
                  >
                    {i + 1}
                  </button>
                ))}
                
                <button 
                  className="page-btn"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </Col>
        </Row>
      </Container>
      
      <Footer />
    </div>
  );
};

export default DonationsListing;